import { ChartSeries, ChartSpec } from 'types/aiChat';
import type { FigureCell, FigureDefinition, FigureRows, FigureRowsColumn } from 'types/aiChatFigure';
import { CHART_COLOR_TOKENS, ChartColorToken } from 'utils/chartColors';

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isChartColorToken = (value: unknown): value is ChartColorToken =>
    CHART_COLOR_TOKENS.includes(value as ChartColorToken);

const parseStringArray = (value: unknown): string[] | null => {
    if (!Array.isArray(value) || value.length === 0) return null;
    if (!value.every((item) => typeof item === 'string')) return null;
    return value;
};

const parseSeriesArray = (value: unknown, expectedLength: number): ChartSeries[] | null => {
    if (!Array.isArray(value) || value.length === 0) return null;

    const parsed = value.map((item) => {
        if (!item || typeof item !== 'object') return null;
        const valueSeries = item as Record<string, unknown>;

        if (typeof valueSeries.label !== 'string' || !Array.isArray(valueSeries.data)) return null;
        if (valueSeries.data.length !== expectedLength) return null;
        if (!valueSeries.data.every((datum) => isFiniteNumber(datum))) return null;

        // No colour arrives as an explicit null, not as a missing key, so normalise before testing.
        // A colour outside the palette means the producer and this parser disagree about the
        // contract; drawing the series in whatever the palette hands out would hide that.
        const rawColor = valueSeries.color ?? undefined;
        const color = isChartColorToken(rawColor) ? rawColor : undefined;
        if (color === undefined && rawColor !== undefined) return null;

        return {
            label: valueSeries.label,
            data: valueSeries.data as number[],
            color,
        };
    });

    if (parsed.some((series) => !series)) return null;
    return parsed as ChartSeries[];
};

const parsePieData = (
    value: unknown
):
    | {
          label: string;
          value: number;
      }[]
    | null => {
    if (!Array.isArray(value) || value.length === 0) return null;

    const parsed = value.map((item) => {
        if (!item || typeof item !== 'object') return null;
        const valueDatum = item as Record<string, unknown>;

        if (typeof valueDatum.label !== 'string' || !isFiniteNumber(valueDatum.value)) return null;

        return {
            label: valueDatum.label,
            value: valueDatum.value,
        };
    });

    if (parsed.some((datum) => !datum)) return null;
    return parsed as { label: string; value: number }[];
};

export const parseChartSpec = (value: unknown): ChartSpec | null => {
    if (!value || typeof value !== 'object') return null;

    const spec = value as Record<string, unknown>;
    const type = typeof spec.type === 'string' ? spec.type : '';

    if (type !== 'bar' && type !== 'line' && type !== 'pie') return null;

    const title = typeof spec.title === 'string' ? spec.title : undefined;
    const description = typeof spec.description === 'string' ? spec.description : undefined;

    if (type === 'bar' || type === 'line') {
        const x = parseStringArray(spec.x);
        if (!x) return null;

        const series = parseSeriesArray(spec.series, x.length);
        if (!series) return null;

        return {
            type,
            title,
            description,
            x,
            series,
            stacked: typeof spec.stacked === 'boolean' ? spec.stacked : undefined,
            zeroBaseline: typeof spec.zeroBaseline === 'boolean' ? spec.zeroBaseline : undefined,
        };
    }

    const data = parsePieData(spec.data);
    if (!data) return null;

    return {
        type,
        title,
        description,
        data,
    };
};

export const parseChartSpecFromToolOutput = (output: unknown): ChartSpec | null => {
    const direct = parseChartSpec(output);
    if (direct) return direct;

    if (!output || typeof output !== 'object') return null;
    if (!('chart' in output)) return null;

    return parseChartSpec((output as Record<string, unknown>).chart);
};

/** An inline chart Figure's own values as the spec the chart draws, checked as an older chat's chart tool output is. */
export const inlineChartSpec = (definition: FigureDefinition): ChartSpec | null =>
    parseChartSpec({
        type: definition.chartType,
        title: definition.title,
        x: definition.inline?.x,
        series: definition.inline?.series,
        data: definition.inline?.data,
        stacked: definition.stacked,
        zeroBaseline: definition.zeroBaseline,
    });

// A sourced pie past this many slices keeps the largest and folds the rest into one, as the backend folds an inline pie.
const MAX_SLICES = 50;

const numberOf = (cell: FigureCell | undefined): number => {
    const value = typeof cell === 'object' && cell !== null && 'amount' in cell ? Number(cell.amount) : cell;
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

/** The distinct values of a column in the rows' order, numbers (years, months) in their own order. */
const distinct = (rows: FigureCell[][], index: number, text: (row: FigureCell[]) => string): string[] => {
    const seen = new Map<string, FigureCell>();
    rows.forEach((row) => {
        if (!seen.has(text(row))) {
            seen.set(text(row), row[index] ?? null);
        }
    });
    const values = [...seen];
    if (values.every(([, cell]) => typeof cell === 'number')) {
        values.sort(([, a], [, b]) => Number(a) - Number(b));
    }
    return values.map(([key]) => key);
};

interface SourcedLabels {
    /** A category or series cell as the chart names it: a record by its label, a month by its name. */
    cell: (cell: FigureCell, column: FigureRowsColumn) => string;
    /** The one series' name when no field splits the values. */
    value: string;
    /** The slice the smallest slices of a large pie fold into. */
    other: string;
}

/**
 * A sourced chart's rows as the spec the chart draws: `x` names the categories, `y` the values, and a `series` field
 * splits them into one series per value, so one aggregation by year and month draws this year against last year.
 * Every row is drawn, since the source's own row limit caps the chart and not the inline chart's 50 points and 5
 * series: past five series the palette repeats. A pie past 50 slices keeps its 49 largest and folds the rest.
 */
export const sourcedChartSpec = (
    definition: FigureDefinition,
    { columns, rows }: FigureRows,
    labels: SourcedLabels
): ChartSpec | null => {
    const indexOf = (field: string | undefined) => (field ? columns.findIndex((column) => column.field === field) : -1);
    const [xAt, yAt, seriesAt] = [indexOf(definition.x), indexOf(definition.y), indexOf(definition.series)];
    if (xAt < 0 || yAt < 0) {
        return null;
    }
    const { title, stacked, zeroBaseline } = definition;
    const type = definition.chartType ?? 'bar';
    const category = (row: FigureCell[]) => labels.cell(row[xAt] ?? null, columns[xAt]);

    if (type === 'pie') {
        const data = rows.map((row) => ({ label: category(row), value: numberOf(row[yAt]) }));
        if (data.length <= MAX_SLICES) {
            return { type, title, data };
        }
        const largest = data.toSorted((a, b) => b.value - a.value);
        const rest = largest.slice(MAX_SLICES - 1).reduce((total, slice) => total + slice.value, 0);
        return { type, title, data: [...largest.slice(0, MAX_SLICES - 1), { label: labels.other, value: rest }] };
    }
    if (seriesAt < 0) {
        const series = [{ label: labels.value, data: rows.map((row) => numberOf(row[yAt])) }];
        return { type, title, x: rows.map(category), series, stacked, zeroBaseline };
    }

    const seriesOf = (row: FigureCell[]) => labels.cell(row[seriesAt] ?? null, columns[seriesAt]);
    const x = distinct(rows, xAt, category);
    const values = new Map<string, number>();
    rows.forEach((row) => {
        const key = `${seriesOf(row)}\u0000${category(row)}`;
        values.set(key, (values.get(key) ?? 0) + numberOf(row[yAt]));
    });
    const series = distinct(rows, seriesAt, seriesOf).map((label) => ({
        label,
        data: x.map((at) => values.get(`${label}\u0000${at}`) ?? 0),
    }));
    return { type, title, x, series, stacked, zeroBaseline };
};
